import ExcelJS from "exceljs";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  triggerBlobDownload,
} from "./medicalSheetCommon";
import { CUSTOMER_TYPE } from "../constants/customer.constant";
import { getSiteDisplayName } from "../constants/siteDisplayNames";
import { formatTbCustomerName } from "./tbSheetExport";
import {
  cloneAdmissionRecords,
  compareMedicalFormCustomersByName,
  customerNameFields,
  formatAdmissionDateDisplay,
  normalizeSheetCellText,
} from "./medicalCustomerSheetSave";

export const AUDIT_HEADERS = [
  "No.",
  "Customer",
  "Medicaid Number",
  "Admission Date",
  "Audit Month",
];

const BASE_COL_WIDTHS_PX = [48, 280, 160, 130, 140];
const BORDER_80 = "FF333333";
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const border80 = {
  top: { style: "thin", color: { argb: BORDER_80 } },
  left: { style: "thin", color: { argb: BORDER_80 } },
  bottom: { style: "thin", color: { argb: BORDER_80 } },
  right: { style: "thin", color: { argb: BORDER_80 } },
};

function formatExportedOnDate(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${MONTHS[valid.getMonth()]} ${String(valid.getDate()).padStart(2, "0")}, ${valid.getFullYear()}`;
}

export function buildAuditTitle(siteNum, exportedAt = new Date()) {
  const site = getSiteDisplayName(siteNum);
  return `${site} Audit (Exported on ${formatExportedOnDate(exportedAt)})`;
}

function parseAdmissionParts(raw) {
  const text = `${raw ?? ""}`.trim();
  if (!text) return null;
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return { month: Number(iso[2]), day: Number(iso[3]), year: Number(iso[1]) };
  }
  const mdy = text.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (mdy) {
    let year = Number(mdy[3]);
    if (String(mdy[3]).length === 2) year = 2000 + year;
    return { month: Number(mdy[1]), day: Number(mdy[2]), year };
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
    year: parsed.getFullYear(),
  };
}

function formatAdmissionDate(raw) {
  return formatAdmissionDateDisplay(raw);
}

function auditMonthFromAdmission(raw) {
  const parts = parseAdmissionParts(raw);
  if (!parts || parts.month < 1 || parts.month > 12) return "";
  return MONTHS[parts.month - 1];
}

function auditMonthSortValue(raw) {
  const parts = parseAdmissionParts(raw);
  if (!parts || parts.month < 1 || parts.month > 12) return 13;
  return parts.month;
}

function rawAdmissionDate(customer) {
  const records = customer?.admission_records;
  if (Array.isArray(records) && records.length > 0) {
    const first = `${records[0]?.admission_date ?? ""}`.trim();
    if (first) return first;
  }
  return `${customer?.admission_date ?? ""}`.trim();
}

export function isAuditRosterCustomer(customer) {
  if (!customer) return false;
  const status = `${customer.status || ""}`.trim().toLowerCase();
  if (status !== "active") return false;
  const type = `${customer.type || ""}`.trim().toLowerCase();
  if (type !== CUSTOMER_TYPE.MEMBER) return false;
  const program = `${customer.program_type || customer.program || ""}`.trim().toLowerCase();
  return program === "amdc";
}

export function buildAuditRows(customers = []) {
  const eligible = (customers || []).filter(isAuditRosterCustomer);
  eligible.sort((a, b) => {
    const monthA = auditMonthSortValue(rawAdmissionDate(a));
    const monthB = auditMonthSortValue(rawAdmissionDate(b));
    if (monthA !== monthB) return monthA - monthB;
    return compareMedicalFormCustomersByName(a, b);
  });
  return eligible.map((customer, index) => {
    const admissionRaw = rawAdmissionDate(customer);
    return {
      no: index + 1,
      id: `${customer?._id ?? customer?.id ?? ""}`,
      ...customerNameFields(customer),
      name: formatTbCustomerName(customer),
      medicaidNumber: `${customer?.medicaid_number ?? ""}`.trim(),
      admissionDate: formatAdmissionDate(admissionRaw),
      admissionRecords: cloneAdmissionRecords(customer),
      auditMonth: auditMonthFromAdmission(admissionRaw),
    };
  });
}

export function buildAuditSheetModel(rows, siteNum, exportedAt = new Date()) {
  return {
    title: buildAuditTitle(siteNum, exportedAt),
    headers: AUDIT_HEADERS,
    rows: (rows || []).map((row) => [
      `${row.no ?? ""}`,
      `${row.name ?? ""}`,
      `${row.medicaidNumber ?? ""}`,
      `${row.admissionDate ?? ""}`,
      `${row.auditMonth ?? ""}`,
    ]),
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: "Audit",
    headerRowHeightPx: 36,
    halignByColumn: {
      0: 0,
      1: 1,
      2: 0,
      3: 0,
      4: 0,
    },
  };
}

export function buildAuditFortuneSheetData(rows, siteNum, exportedAt = new Date()) {
  return buildMedicalFortuneSheetData(buildAuditSheetModel(rows, siteNum, exportedAt));
}

export function applyAuditDerivedFields(row) {
  const admissionDate = formatAdmissionDateDisplay(row?.admissionDate);
  return {
    ...row,
    admissionDate,
    auditMonth: auditMonthFromAdmission(admissionDate),
  };
}

const AUDIT_COLUMN_COUNT = AUDIT_HEADERS.length;

export function parseAuditRowsFromWorkbook(workbook, baselineRows = []) {
  const sheetRows = workbook?.[0]?.data;
  if (!Array.isArray(sheetRows)) return baselineRows;
  const parsed = [];
  for (let i = 0; i < baselineRows.length; i += 1) {
    const row = sheetRows[i + 2] || [];
    const baseline = baselineRows[i] || {};
    const cells = [];
    for (let c = 0; c < AUDIT_COLUMN_COUNT; c += 1) {
      cells.push(normalizeSheetCellText(row[c]));
    }
    if (!cells.some((cell) => cell)) {
      parsed.push({ ...baseline });
      continue;
    }
    parsed.push(
      applyAuditDerivedFields({
        ...baseline,
        no: Number(cells[0]) || baseline.no || i + 1,
        name: baseline.name || "",
        medicaidNumber: cells[2],
        admissionDate: cells[3],
      })
    );
  }
  return parsed;
}

export function auditRowsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i] || {};
    const right = b[i] || {};
    const keys = ["id", "name", "medicaidNumber", "admissionDate", "auditMonth"];
    for (const key of keys) {
      if (`${left[key] ?? ""}`.trim() !== `${right[key] ?? ""}`.trim()) return false;
    }
  }
  return true;
}

export function collectAuditCustomerUpdates(draftRows = [], baselineRows = []) {
  const byId = new Map((baselineRows || []).map((row) => [`${row.id}`, row]));
  const updates = [];
  (draftRows || []).forEach((row) => {
    const id = `${row?.id ?? ""}`.trim();
    if (!id) return;
    const baseline = byId.get(id);
    if (!baseline) return;
    const payload = {};
    if (`${row.medicaidNumber ?? ""}`.trim() !== `${baseline.medicaidNumber ?? ""}`.trim()) {
      payload.medicaid_number = `${row.medicaidNumber ?? ""}`.trim();
    }
    if (Object.keys(payload).length > 0) {
      updates.push({ customerId: id, ...payload });
    }
  });
  return updates;
}

export async function downloadAuditPdf(rows, siteNum, filename = "Audit") {
  const model = buildAuditSheetModel(rows, siteNum, new Date());
  await downloadMedicalTablePdf({
    title: model.title,
    headers: model.headers,
    rows: model.rows,
    filename,
    pageOptions: {
      orientation: "portrait",
      fontSize: 10,
      headFontSize: 10,
      titleFontSize: 14,
      marginX: 36,
      marginY: 36,
      lineColor: [51, 51, 51],
    },
    columnStyles: {
      0: { cellWidth: 32, halign: "center" },
      1: { cellWidth: 198, halign: "left" },
      2: { cellWidth: 110, halign: "center" },
      3: { cellWidth: 100, halign: "center" },
      4: { cellWidth: 100, halign: "center" },
    },
  });
}

export async function downloadAuditXlsx(rows, siteNum, filename = "Audit") {
  const model = buildAuditSheetModel(rows, siteNum, new Date());
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet("Audit");
  const colCount = model.headers.length;

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = 1; col <= colCount; col += 1) {
    sheet.getCell(1, col).border = border80;
  }
  sheet.getRow(1).height = 40;
  sheet.pageSetup = {
    paperSize: 1,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
  };

  const headerRow = sheet.getRow(2);
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 12, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  model.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    row.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value || null;
      cell.font = { size: 12, name: "Arial" };
      cell.alignment = {
        horizontal: colIndex === 1 ? "left" : "center",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
    });
  });

  model.columnWidthsPx.forEach((px, index) => {
    sheet.getColumn(index + 1).width = Math.max(8, Math.round(px / 7));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename, ".xlsx")
  );
}
